import type { SchemaField } from '@cli-types/core-engine/schema.js'
import { singularize, toCamelCase, toPascalCase } from '@cli-utils/text.js'
import { isUserRelationshipTarget } from '@core-engine/schema/system-relationships/is-user-relationship-target.js'
import { resolveExtensionNamespace } from '@next-adapter/config/paths/resolve-extension-namespace.js'
import { renderJunctionTableGeneratedValueTemplate } from '@next-adapter/templates/renderers/generators/database/generate-junction-table.js'

export function generateJunctionTable(
  schemaName: string,
  field: SchemaField,
  requiredImports: Set<string>,
  siblingImports: Set<string>,
  currentNamespace: string
): { name: string; content: string } {
  const relationship = field.relationship
  if (!relationship) {
    throw new Error(`Relationship field "${field.name}" is missing "relationship".`)
  }

  const singularSchema = singularize(schemaName)
  const singularRelation = singularize(relationship)
  const junctionName = `${singularSchema}${toPascalCase(relationship)}`
  const junctionPascal = toPascalCase(junctionName)
  const schemaIdCol = `${singularSchema}Id`
  const relationIdCol = `${singularRelation}Id`
  const schemaTableRef = toCamelCase(schemaName)
  const relationTableRef = isUserRelationshipTarget(relationship)
    ? 'user'
    : toCamelCase(relationship)
  const relationIdType = isUserRelationshipTarget(relationship) ? 'text' : 'uuid'

  requiredImports.add('pgTable')
  requiredImports.add('uuid')
  if (relationIdType === 'text') {
    requiredImports.add('text')
  }
  requiredImports.add('primaryKey')
  if (!isUserRelationshipTarget(relationship)) {
    const relationshipNamespace =
      currentNamespace === schemaName ? resolveExtensionNamespace(relationship) : currentNamespace
    const relationshipImport =
      relationshipNamespace === currentNamespace
        ? `./${relationship}`
        : `../${relationshipNamespace}/${relationship}`
    siblingImports.add(`import { ${relationTableRef} } from '${relationshipImport}'`)
  }

  return {
    name: toCamelCase(junctionName),
    content: renderJunctionTableGeneratedValueTemplate({
      toCamelCase: toCamelCase(junctionName),
      junctionPascal: junctionPascal,
      schemaIdCol: schemaIdCol,
      schemaTableRef: schemaTableRef,
      relationIdCol: relationIdCol,
      relationIdType: relationIdType,
      relationTableRef: relationTableRef
    })
  }
}
